import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, Drawer, List, Divider, Link } from "@mui/material";
import { BrandLogo } from "@theatticusapp/atticus-shared-components";
import NavItem from "../../shared/NavItem";
import {
  AppScreen,
  estateAccessNavItems,
  profileSettingsNavItems,
} from "./navItems";
import { useAuthContext } from "../../../AuthContextWrapper";

interface Props {
  // user: User;
  drawerWidth: number;
  onMobileClose?: () => void;
  openMobile: boolean;
}

interface NavItemSection {
  title: string;
  items: AppScreen[];
}

export const AppSideBar = ({
  drawerWidth,
  onMobileClose,
  openMobile = false,
}: Props): JSX.Element => {
  const location = useLocation();
  const { activeEstate, user } = useAuthContext()

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const allNavItems: NavItemSection[] = [
    {
      title: "Estate Access",
      items: estateAccessNavItems,
    },
    {
      title: "Profile Settings",
      items: profileSettingsNavItems,
    },
  ];

  // If the Advisor Group does not allow for forms, remove the Nav Item.
  if (activeEstate?.advisorGroup?.formsDisabled) {
    const estateAccessNavGroup = allNavItems.find(
      (section) => (section.title = "Estate Access")
    );
    if (estateAccessNavGroup) {
      estateAccessNavGroup.items = estateAccessNavItems.filter(
        (item) => item.title !== "Forms"
      );
    }
  }

  const getContent = (logo: boolean): JSX.Element => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      className="app-sidebar-drawer-content"
    >
      {logo && (
        <Box
          sx={{
            p: 2,
          }}
        >
          <Link href="/app/home">
            <BrandLogo
              advisorGroupKey={
                activeEstate?.advisorGroup?.title || user?.advisorGroupName
              }
              secondary
              sx={{ maxWidth: "6.75rem" }}
            />
          </Link>
        </Box>
      )}
      <Box
        sx={{
          my: 1,
          mx: 0,
        }}
      >
        <List>
          {Object.values(allNavItems).map((section, index) => {
            return (
              <React.Fragment key={section.title}>
                {section.items.map((screen: AppScreen) => (
                  <React.Fragment key={screen.title}>
                    <NavItem
                      path={screen.path}
                      title={screen.title}
                      icon={screen.icon}
                    />
                  </React.Fragment>
                ))}
                {/* Add Divider between sections, not including end fenceposts */}
                {index < allNavItems.length - 1 && allNavItems.length > 1 && (
                  <Divider
                    sx={{
                      my: 2,
                    }}
                    variant="middle"
                    key="NavBar-divider"
                  />
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
      <Box flexGrow={1} />
      <Divider variant="middle" key="AppSideBar-divider2" />
      {/*<AdvisorMenuAndSignOut />*/}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      aria-label="mailbox folders"
    >
      <Drawer
        variant="temporary"
        open={openMobile}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", sm: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
          },
        }}
        className="app-mobile-drawer"
      >
        {getContent(false)}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            boxShadow: "none",
          },
        }}
        className="app-stationary-drawer"
        open
      >
        {getContent(true)}
      </Drawer>
    </Box>
  );
};

// const mapStateToProps = ({ user }: StoreState): { user: User } => {
//   return { user };
// };
//
// export const FlowSideBar = connect(mapStateToProps)(_NavBar);
